import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, MessageCircle, Mic, Camera,
  Clock, Sparkles, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { getSubjectVisual, resolveSubjectName } from '@/lib/subjectVisuals';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function ChatHome() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [school, setSchool] = useState(null);

  useEffect(() => {
    fetchConversations();
    // Fetch school branding when child is school-enrolled (Tier-1)
    api.get(`/children/${childId}`)
      .then((res) => {
        if (res.data?.school_id) {
          return api.get(`/schools/${res.data.school_id}`).then((s) => setSchool(s.data));
        }
        return null;
      })
      .catch(() => {});
  }, [childId]);

  const fetchConversations = async () => {
    try {
      const res = await api.get(`/chat/conversations/${childId}`);
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = async () => {
    try {
      const res = await api.post('/chat/conversations', { child_id: childId });
      navigate(`/child/${childId}/chat/${res.data.conversation_id}`);
    } catch (err) {
      toast.error('Failed to start conversation');
    }
  };

  const filtered = conversations.filter(c =>
    !searchQuery || c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.topics?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col" data-testid="chat-home">
      {school && (
        <div
          className="w-full bg-white border-b shadow-sm"
          style={{ borderTopWidth: 4, borderTopColor: school.brand_color || '#4F46E5' }}
          data-testid="student-school-stripe"
        >
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-2">
            {school.logo_url ? (
              <img
                src={school.logo_url}
                alt={school.name}
                className="w-6 h-6 rounded object-contain bg-white border"
                data-testid="student-school-logo"
              />
            ) : (
              <div
                className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold"
                style={{ backgroundColor: school.brand_color || '#4F46E5' }}
              >
                {school.name?.charAt(0) || 'S'}
              </div>
            )}
            <span
              className="text-xs font-semibold text-slate-700"
              data-testid="student-school-name-welcome"
            >
              {school.name}
            </span>
          </div>
        </div>
      )}
      {/* Header — full width */}
      <div className="w-full bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 p-4 sm:p-5 text-white shadow-lg">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button
            data-testid="chat-home-back"
            onClick={() => navigate(`/child/${childId}/home`)}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-11 h-11 bg-white/25 backdrop-blur-sm rounded-full flex items-center justify-center"
          >
            <Sparkles className="w-6 h-6" />
          </motion.div>
          <div>
            <h2 className="font-bold text-lg leading-tight">Ask Yukti</h2>
            <p className="text-xs text-white/70">Your curious companion</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <div className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="chat-search"
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white rounded-xl border border-orange-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>

        {/* New Chat Button */}
        <div className="pt-3">
          <Button
            data-testid="new-chat-btn"
            onClick={handleNewChat}
            className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl py-5 shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Conversation
          </Button>
        </div>

        {/* Conversations */}
        <div className="pt-4 pb-24 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-orange-300 border-t-orange-600 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-orange-400" />
              </div>
              <p className="text-gray-600 font-medium">No conversations yet</p>
              <p className="text-gray-400 text-sm mt-1">Start asking Yukti anything!</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AnimatePresence>
                {filtered.map((conv, i) => {
                  const visual = getSubjectVisual(conv.subject);
                  const SubjectIcon = visual.icon;
                  const subjectColor = `${visual.bg} ${visual.text}`;
                  const displaySubject = resolveSubjectName(conv.subject);
                  return (
                    <motion.div
                      key={conv.conversation_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      data-testid={`conversation-${conv.conversation_id}`}
                      onClick={() => navigate(`/child/${childId}/chat/${conv.conversation_id}`)}
                      className="bg-white rounded-xl p-4 shadow-sm border border-orange-100 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all active:scale-[0.98]"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${subjectColor}`}>
                          <SubjectIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-800 text-sm truncate">
                            {conv.title || 'New Conversation'}
                          </h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {displaySubject && (
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${subjectColor}`}>
                                {displaySubject}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {timeAgo(conv.updated_at)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {conv.message_count || 0} msgs
                            </span>
                          </div>
                          {conv.topics?.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {conv.topics.slice(0, 3).map(t => (
                                <span key={t} className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-orange-100 py-3 z-10">
        <div className="max-w-5xl mx-auto flex gap-2 justify-center px-4">
          <button
            data-testid="quick-text"
            onClick={handleNewChat}
            className="flex items-center gap-1.5 px-5 py-2 bg-orange-50 rounded-full text-xs text-orange-600 font-medium hover:bg-orange-100 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Type
          </button>
          <button
            data-testid="quick-voice"
            onClick={handleNewChat}
            className="flex items-center gap-1.5 px-5 py-2 bg-pink-50 rounded-full text-xs text-pink-600 font-medium hover:bg-pink-100 transition-colors"
          >
            <Mic className="w-3.5 h-3.5" /> Voice
          </button>
          <button
            data-testid="quick-photo"
            onClick={handleNewChat}
            className="flex items-center gap-1.5 px-5 py-2 bg-purple-50 rounded-full text-xs text-purple-600 font-medium hover:bg-purple-100 transition-colors"
          >
            <Camera className="w-3.5 h-3.5" /> Photo
          </button>
        </div>
      </div>
    </div>
  );
}
